import { defineStore } from "pinia";

const COOKIE_HTTP_ONLY = !process.dev;
const COOKIE_SECURE = !process.dev;
const getCookieMaxAge = () => {
  const runtimeConfig = useRuntimeConfig();
  return runtimeConfig.public.CookieMaxAge;
};
const getCookieOptions = () => ({
  maxAge: getCookieMaxAge(),
  secure: COOKIE_SECURE,
  httpOnly: COOKIE_HTTP_ONLY,
  sameSite: 'strict' as const
});

const getCookieStorage = () =>
  piniaPluginPersistedstate.cookies({
    maxAge: getCookieMaxAge(),
    secure: COOKIE_SECURE,
    httpOnly: COOKIE_HTTP_ONLY,
    sameSite: 'strict'
  });

const storage = {
  getItem: (key: string) => {
    if (process.client && COOKIE_HTTP_ONLY) {
      return null;
    }
    return getCookieStorage().getItem(key);
  },
  setItem: (key: string, value: string) => {
    if (process.client && COOKIE_HTTP_ONLY) {
      void fetch('/api/persist-cookie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value, options: getCookieOptions() })
      }).catch((error) => {
        console.error('Failed to persist location cookie', error);
      });
      return;
    }
    getCookieStorage().setItem(key, value);
  },
  removeItem: (key: string) => {
    if (process.client && COOKIE_HTTP_ONLY) {
      void fetch('/api/persist-cookie', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      }).catch((error) => {
        console.error('Failed to remove location cookie', error);
      });
      return;
    }
    getCookieStorage().removeItem(key);
  }
};

const hydrateLocationFromApi = (context: { store: any }) => {
  if (!process.client || !COOKIE_HTTP_ONLY) return;
  const key = context.store.$id;
  void (async () => {
    const { value } = await $fetch<{ value?: string }>('/api/persist-cookie', {
      method: 'GET',
      query: { key }
    }).catch(() => ({ value: undefined }));
    if (!value) return;
    try {
      const deserialized = JSON.parse(value);
      context.store.$patch(deserialized);
    } catch (error) {
      console.error('Failed to hydrate location cookie', error);
    }
  })();
};

interface GeoResponse {
  data: Array<any>;
}

const hasUsableGps = (lat: unknown, lng: unknown) => {
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return false;
  }
  const latitude = Number(lat);
  const longitude = Number(lng);
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  );
};

interface Locality {
  code: string | null;
  name: string | null;
}

interface GPS {
  latitude: number | null;
  longitude: number | null;
}

interface Location {
  id:string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_line3: string | null;
  area_code: string | null;
  city: Locality;
  state: Locality;
  country: Locality;
  gps: GPS;
  formatted_address: string | null;
  error: string | null;
  saved_address: {
    address_line1: string | null,
    address_line2: string | null,
    address_line3: string | null,
    area_code: string | null,
    city: { code: string | null, name: string | null },
    state: { code: string | null, name: string | null },
    country: { code: string | null, name: string | null },
    gps: { latitude: number | null, longitude: number | null },
    formatted_address: string | null,
    error: string | null,
  },
}

export const useLocation = defineStore('location', {
  state: () => ({
    id: null,
    address_line1: null,
    address_line2: null,
    address_line3: null,
    area_code: null,
    city: { code: null, name: null },
    state: { code: null, name: null },
    country: { code: null, name: null },
    gps: { latitude: null, longitude: null },
    formatted_address: null,
    error: null,
    saved_address: {
      address_line1: null,
      address_line2: null,
      address_line3: null,
      area_code: null,
      city: { code: null, name: null },
      state: { code: null, name: null },
      country: { code: null, name: null },
      gps: { latitude: null, longitude: null },
      formatted_address: null,
      error: null,
    },  
  }),
  getters: {
    isGPSEnabled(state: any): boolean {
      const lat = Number(state?.gps?.latitude);
      const lng = Number(state?.gps?.longitude);
      const hasValidGps = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
      console.log("Latitude:", lat, "Longitude:", lng, "HasValidGps:", hasValidGps, "City:", state.city);
      if (!state.city) {
        state.city = { code: "std:080" };
      }else if (typeof state.city === "string") {
        state.city = {
          name: state.city,
          code: "std:080",
        };
      } else if (state.city.code == null) {
        state.city.code = "std:080";
      }
     return hasValidGps ;
    },
  },
  actions: {
    async updateError(error: string) {
      this.error = error;
    },

    async saveResolvedAddressText(address: string) {
      try {
        const { data } = await $apiServer("/maps/geocode", {
          method: "post",
          body: { address },
        });
    
        if (!data || data.length === 0) {
          throw new Error("No results found");
        }
        console.log("address", data[0]);

        const addressData = address.split(',').map(text => text.trim());
    
        this.saved_address = {
          address_line1: data[0].door || addressData[0],
          address_line2: data[0].street ?? null,
          address_line3: data[0].locality ?? null,
          area_code: data[0].area_code ?? null,
          city: { code: null, name: data[0].city ?? null },
          state: { code: data[0].state ?? null, name: data[0].state ?? null },
          country: { code: data[0].country ?? null, name: data[0].country ?? null },
          gps: {
            latitude: data[0].latitude ?? null,
            longitude: data[0].longitude ?? null,
          },
          formatted_address: address,
          error: null,
        };
      } catch (error: any) {
        console.error("Error in saveResolvedAddressText:", error.message);
        this.saved_address = {
          ...this.saved_address,
          error: error.message,
        };
      }
    },
    

    async update(data: any) {
      const latitude = Number(data?.latitude);
      const longitude = Number(data?.longitude);
      const hasValidGps =
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        !(latitude === 0 && longitude === 0);

      this.id = data.id ?? null;
      this.address_line1 = data.door ?? null;
      this.address_line2 = data.street ?? null;
      this.address_line3 = data.locality ?? null;
      this.area_code = data.area_code ?? null;
      this.city = { code: null, name: data.city ?? null };
      this.state = { code: data.state ?? null, name: data.state ?? null };
      this.country = { code: data.country ?? null, name: data.country ?? null };
      this.gps = {
        latitude: hasValidGps ? latitude : null,
        longitude: hasValidGps ? longitude : null,
      };
      this.formatted_address = data.formatted_address ?? null;
      console.log("the selecte city", data.city);
      if (data.city) {
        await this.setCityCode(data.city);
      }

      if(!data.selectLocation) {

      const checkout = useCheckout();
      checkout.setDeliveryAddress({
        name: data.name ?? null,
        phone: data.phone ?? null,
        email: data.email ?? null,
        address_line1: data.door ?? null,
        address_line2: data.street ?? null,
        address_line3: data.locality ?? null,
        area_code: data.area_code ?? null,
        city: data.city?? null,
        state: data.state?? null,
        label: data?.label,
        address_id: data?.id
      });
      }

      const userlocsRaw = localStorage.getItem("userlocs");
      const userlocs: any[] = userlocsRaw ? JSON.parse(userlocsRaw) : [];
      const uniqueAddressesMap = new Map(
        userlocs.map((addr) => [addr.formatted_address, addr])
      );
      uniqueAddressesMap.delete(this.formatted_address);
      const uniqueAddresses = Array.from(uniqueAddressesMap.values());
      if (uniqueAddresses.length === 5) {
        uniqueAddresses.pop();
      }
      uniqueAddresses.unshift({
        address_line1: this.address_line1,
        address_line2: this.address_line2,
        address_line3: this.address_line3,
        area_code: this.area_code,
        city: this.city,
        state: this.state,
        country: this.country,
        gps: this.gps,
        formatted_address: this.formatted_address,
      });
      localStorage.setItem("userlocs", JSON.stringify(uniqueAddresses));
    },
    async setAddress(address: any) {
      try {
        const providedLatitude = address?.gps?.latitude;
        const providedLongitude = address?.gps?.longitude;
        const hasProvidedGps = hasUsableGps(providedLatitude, providedLongitude);

        if (hasProvidedGps) {
          this.update({
            name: address.name,
            phone: address.phone,
            email: address.email,
            door: address.address_line1,
            street: address.address_line2,
            locality: address.address_line3,
            area_code: address.area_code,
            city: address.city,
            state: address.state,
            country: 'IND',
            latitude: Number(providedLatitude),
            longitude: Number(providedLongitude),
            label: address.label,
            formatted_address: [
              address.address_line1,
              address.address_line2,
              address.address_line3,
              address.city,
              address.state,
              address.area_code,
            ].filter(Boolean).join(', '),
          });
          return;
        }

        const addressArray = [
          address.address_line1,
          address.address_line2,
          address.address_line3,
          address.city,
          address.state,
          address.area_code,
        ];
        let apiResult: any = null;
        try {
          const { data } = (await $apiServer("/maps/geocode", {
            method: "post",
            body: { address: addressArray.join(" ") },
          })) as GeoResponse;
          if (!data || data.length === 0) {
            throw new Error("No results found");
          }
          apiResult = data;
        } catch (error) {
          console.error("Error in setAddress geocode:", error);
        }

        this.update({
          name: address.name,
          phone: address.phone,
          email: address.email,
          door: address.address_line1,
          street: address.address_line2,
          locality: address.address_line3,
          area_code: address.area_code,
          city: address.city,
          state: address.state,
          country: apiResult?.[0]?.country || 'IND',
          latitude: apiResult?.[0]?.latitude ?? null,
          longitude: apiResult?.[0]?.longitude ?? null,
          label: address.label,
          formatted_address: apiResult?.[0].formatted_address || [
            address.address_line1,
            address.address_line2,
            address.address_line3,
            address.city,
            address.state,
            address.area_code,
          ].filter(Boolean).join(', '),
        });
      } catch (error: any) {
        console.error("Error in setAddress:", error.message);
        throw error;
      }
    },
    async setAddressText(address: any,addressObject: any) {
      //this.$reset();
      try {
        const providedLatitude = addressObject?.gps?.latitude;
        const providedLongitude = addressObject?.gps?.longitude;
        const hasProvidedGps = hasUsableGps(providedLatitude, providedLongitude);

        if (hasProvidedGps) {
          this.update({
            door: addressObject?.door,
            street: addressObject?.building,
            locality: addressObject?.locality,
            area_code: addressObject?.area_code,
            city: addressObject?.city,
            state: addressObject?.state,
            country: 'IND',
            latitude: Number(providedLatitude),
            longitude: Number(providedLongitude),
            formatted_address: address,
            id: addressObject?.id,
            label: addressObject?.label,
            selectLocation: addressObject?.selectLocation ?? null
          });
          return;
        }

        const { data } = (await $apiServer("/maps/geocode", {
          method: "post",
          body: { address: address },
        })) as GeoResponse;
        if (!data || data?.length === 0) {
          throw new Error("No results found");
        }
        
        this.update({
          door: addressObject?.door || data[0].door ,
          street: addressObject?.building || data[0].street,
          locality: addressObject?.locality || data[0].locality,
          area_code: addressObject?.area_code || data[0].area_code,
          city: addressObject?.city || data[0].city,
          state: addressObject?.state||data[0].state,
          country: data[0].country,
          latitude: data[0].latitude,
          longitude: data[0].longitude,
          formatted_address: address,
          id: addressObject?.id,
          label: addressObject?.label,
          selectLocation: addressObject?.selectLocation ?? null
        });
      } catch (error: any) {
        console.error("Error in setAddress:", error.message);
        throw error;
      }
    },
    
    async setCoords(lat: number, lng: number) {
      try {
        const { data } = (await $apiServer("/maps/geocode/reverse", {
          method: "post",
          body: { lat, lng },
        })) as GeoResponse;
        if (!data || data?.length === 0) {
          throw new Error("No results found");
        }
        data[0].selectLocation = true;
        await this.update(data[0]);
      } catch (error: any) {
        console.error("Error in setCoords:", error.message);
        throw error;
      }
    },
    async setCityCode(name: string) {
      try {
        const { data } = (await $apiServer("/cities/search", {
          query: { name: encodeURIComponent(name) },
        })) as GeoResponse;
        if (!data || data?.length === 0) {
          throw new Error("City not found");
        }

        this.city = { code: data[0].code, name };
      } catch (error: any) {
        console.error("Error in setCityCode:", error.message);
        this.city = { code: "std:080", name };
      }
    },
    async setAddressByAreaCode(area_code: string) {
      try {
        // const addressArray = [area_code];
        const { data } = (await $apiServer("/maps/geocode", {
          method: "post",
          body: { address: area_code },
        })) as GeoResponse;
        if (!data || data.length === 0) {
          throw new Error("No results found");
        }

        this.update({
          door: data[0].address_line1,
          street: data[0].address_line2,
          locality: data[0].address_line3,
          area_code: area_code,
          city: data[0].city,
          state: data[0].state,
          country: data[0].country,
          latitude: data[0].latitude,
          longitude: data[0].longitude,
          formatted_address: data[0].formatted_address,
        });
      } catch (error: any) {
        console.error("Error in setAddress:", error.message);
        throw error;
      }
    },
    async setSavedAddress(data: any) {
      this.id = data.id || '';
      this.address_line1 = data.address_line1;
      this.address_line2 = data.address_line2;
      this.address_line3 = data.address_line3;
      this.area_code = data.area_code;
      this.city = data.city;
      this.state = data.state;
      this.country = data.country;
      this.gps = {
        latitude: data?.gps?.latitude ?? null,
        longitude: data?.gps?.longitude ?? null,
      };
      this.formatted_address = data.formatted_address;

      const checkout = useCheckout();
      checkout.setDeliveryAddress({
        address_line1: data.address_line1 ?? null,
        address_line2: data.address_line2 ?? null,
        address_line3: data.address_line3 ?? null,
        area_code: data.area_code ?? null,
        city: typeof data.city === 'object' ? data.city.name : data.city,
        state: data.state,
        country: data.country,
        gps: {
          latitude: data?.gps?.latitude ?? null,
          longitude: data?.gps?.longitude ?? null,
        },
        formatted_address: data.formatted_address,
        label: data.label,
        address_id: data.id,
        name: data.name,
        phone: data.phone,
        email: data.email
      });
    },
  },
  persist: {
    storage,
    afterHydrate: hydrateLocationFromApi,
  },
});
